import React, { Component } from 'react';
import { createPortal } from 'react-dom';

import cx from 'classnames';
import _debounce from 'lodash.debounce';
import _memoize from 'lodash.memoize';
import memoizeOne from 'memoize-one';
import { SizeMe } from 'react-sizeme';
import SVG from 'react-inlinesvg';
import Icon from '../../Icon';
import Filters from './Filters';
import Placeholder from './Placeholder';
import BulkActions from '../../BulkActions';
import CardItem from './Card/components/CardItem';
import CardCaption from './CardCaption';
import ListActionButton from './ListActionButton';
import ListItem from './ListItem';
import PitchListItem from './PitchListItem';
import EmptyDataPlaceholder from './EmptyDataPlaceholder';
import Checkbox from '../../Checkbox';
import shouldAbsorbSelectedFilters from '../helpers/shouldAbsorbSelectedFilters';
import { GRID_VIEW, LIST_VIEW, PITCH_LIST_VIEW } from '../constants/viewTypes';
import { LIST_CONTAINER, CARDS_GRID } from '../styles/list';
import { empty } from '../../../assets/images';
import Text from '../../Text';

import { isListMobile } from '../utils/list';
import { InfiniteScroll } from '../../InfiniteScroll';


import '../styles/_list.scss';
























































































const LOADER_INITIAL_TIMEOUT = 1500;
const LOADER_LOAD_MORE_TIMEOUT = 500;
const NO_INFINITE_LOAD_ITEMS = 3;
const GRID_NODE_SELECTOR = '.list-container__content__item > *';

const setNoDataPlaceholder = ({
  renderNoDataPlaceholder,
  isMobile
}) =>
<div
  className={cx('list__no-data-container', {
    'list__no-data-container--mobile': isMobile
  })}>

    {renderNoDataPlaceholder ?
  renderNoDataPlaceholder() :

  <>
        <div className="list__no-data__svg-container">
          <SVG cacheRequests src={empty} uniquifyIDs title="There's nothing here yet." />
        </div>
        <Text heading3 centered noMargin="top">
          There&apos;s nothing here yet, but we&apos;re excited you&apos;re
          here!
        </Text>
      </>}

  </div>;


export default class List extends Component {




  handleChangeView = _memoize((view) => () => {
    const { history } = this.props;

    history.push(`#${view}`);

    if (view === GRID_VIEW) {
      this.setGridNodes();
    }
  });

  static defaultProps = {
    data: [],
    filters: {},
    sortFilters: null,
    advancedFilters: [],
    listKey: 'id'
  };

  constructor(props) {
    super(props);
    this._infiniteScrollParentRef = React.createRef();

    const { selectedFilters, selectedItems, searchTerm } = props;

    this.state = {
      selectedFilters: selectedFilters || {},
      searchTerm: searchTerm || '',
      bothViewsAvailable: Boolean(props.renderCardItem && props.renderListItem),
      usePitchListView: Boolean(
        !(props.renderCardItem && props.renderListItem) &&
        props.renderPitchListItem
      ),
      selectedItems: selectedItems || {},
      isSelectAllActive: false,
      gridNodes: [],
      hasBulkActions: props.canSelectItems || Boolean(props.bulkActions),
      loading: false
    };

    if (props.loading) {
      this.setLoading();
    }
  }

  componentDidMount() {
    this.setGridNodes();
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, loading, searchTerm: newSearchTerm = '' } = this.props;
    const { searchTerm, selectedFilters, loading: stateLoading } = this.state;

    // A parent driving the filters from outside (e.g. Traffic's summary cards) is a second
    // author of state this component otherwise owns alone. Absorb their value so the filter
    // panel below reflects it — Filters passes it on to AdvancedFilters' selectedFiltersProps.
    //
    // Opt-in only. List is otherwise uncontrolled; absorbing for every consumer would hand
    // filter-resync (and a re-grid) to callers that never asked for it. Only a caller that
    // sets controlledSelectedFilters gets the second-author behaviour.
    if (
      this.props.controlledSelectedFilters &&
      shouldAbsorbSelectedFilters(prevProps.selectedFilters, this.props.selectedFilters)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedFilters: this.props.selectedFilters || {} });
    }

    if (!loading && prevProps.loading) {
      this.attemptToClearLoadingTimer();
    }

    if (!loading && prevProps.loading && stateLoading) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ loading: false });
    }

    if (loading && !prevProps.loading && !stateLoading) {
      this.setLoading();
    }

    if (searchTerm !== newSearchTerm) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ searchTerm: newSearchTerm });
    }

    if (
    prevProps.data !== data ||
    prevState.searchTerm !== searchTerm ||
    prevState.selectedFilters !== selectedFilters)
    {
      this.setGridNodes();
      this.hideSelectedItems();
    }

    if (prevProps.data !== data && this.state.isSelectAllActive) {
      this.selectNewlyLoadedItems(prevProps.data);
    }

    this.syncSelectedItemsFromDrawer(prevProps);
  }

  // In project-brief / Pro-workflow flows the drawer (selectedDrawerItemIds)
  // is the source of truth for which assets are "selected" — the per-card
  // checkbox toggles drawer state directly and bypasses List's internal
  // selectedItems. Without this sync, BulkActions would show a stale count
  // and the master checkbox would not reflect drawer-driven changes.
  syncSelectedItemsFromDrawer = (prevProps) => {
    const { isProjectBrief, selectedDrawerItemIds, data, listKey } = this.props;
    if (!isProjectBrief) {return;}

    const prevIds = prevProps.selectedDrawerItemIds || [];
    const newIds = selectedDrawerItemIds || [];

    const idsChanged =
    prevIds.length !== newIds.length ||
    prevIds.some((id, i) => id !== newIds[i]);

    if (!idsChanged && prevProps.data === data) {return;}

    const newSelected = (data || []).reduce((acc, item) => {
      if (newIds.includes(item[listKey])) {
        acc[item[listKey]] = item;
      }
      return acc;
    }, {});

    const stateSelected = this.state.selectedItems;
    const stateKeys = Object.keys(stateSelected).filter((k) => stateSelected[k]);
    const newKeys = Object.keys(newSelected);

    const sameSize = stateKeys.length === newKeys.length;
    const sameContents = sameSize && newKeys.every((k) => stateSelected[k]);

    if (sameContents) {return;}

    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({
      selectedItems: newSelected,
      // If drawer is empty, leave Select-All mode so newly loaded items
      // don't get auto-added.
      isSelectAllActive:
      newKeys.length === 0 ? false : this.state.isSelectAllActive
    });
  };

  selectNewlyLoadedItems = (prevData) => {
    const { data, listKey } = this.props;
    const prevIds = new Set((prevData || []).map((item) => item[listKey]));
    const newlyLoaded = data.filter((item) => !prevIds.has(item[listKey]));

    if (!newlyLoaded.length) {
      return;
    }

    // eslint-disable-next-line react/no-did-update-set-state
    this.setState(
      ({ selectedItems }) => ({
        selectedItems: newlyLoaded.reduce(
          (acc, item) => ({ ...acc, [item[listKey]]: item }),
          selectedItems
        )
      }),
      this.handleItemsSelected
    );
  };

  componentWillUnmount() {
    this.attemptToClearLoadingTimer();
  }

  getFilteredData = memoizeOne(
    (data, selectedFilters, searchTerm, onFiltersChange) => {
      if (onFiltersChange) {
        return data;
      }

      const searchData = data.filter((item) => Object.values(item).some(
        (dataField) => dataField &&
        typeof dataField === 'string' &&
        dataField.toLowerCase().includes(searchTerm.toLowerCase())
      )
      );

      return searchData.filter((item) => {
        const allFilters = Object.values(selectedFilters);
        const filterResults = allFilters.map(
          ({ value }) => value instanceof Function ? value(item) : true
        );

        return filterResults.filter((val) => val).length === filterResults.length;
      });
    }
  );

  setLoading = (timeout) => {
    this.attemptToClearLoadingTimer();
    this._timeout = setTimeout(
      this.showLoader,
      timeout || LOADER_INITIAL_TIMEOUT
    );
  };

  setGridNodes() {
    const { location, bulkActions, canSelectItems } = this.props;

    if (
    !bulkActions && !canSelectItems ||
    !this.getView(location.hash) === GRID_VIEW)
    {
      return;
    }

    setTimeout(() => {
      this.setState({
        gridNodes: [...document.querySelectorAll(GRID_NODE_SELECTOR)]
      });
    }, 0);
  }

  showLoader = () => {
    this.setState({ loading: true });
  };

  attemptToClearLoadingTimer = () => {
    if (!this._timeout) {
      return;
    }

    clearTimeout(this._timeout);

    this._timeout = null;
  };

  hideSelectedItems = () => {
    const { data, listKey, bulkActions, onFiltersChange } = this.props;
    const { selectedFilters, searchTerm } = this.state;
    const filteredData = this.getFilteredData(
      data,
      selectedFilters,
      searchTerm,
      onFiltersChange
    );

    if (!bulkActions) {
      return;
    }

    const { selectedItems } = this.state;

    const newSelected = Object.entries(selectedItems).reduce(
      (selected, [itemId, isSelected]) => {
        if (!isSelected) {
          return selected;
        }

        const dataItem = filteredData.find((item) => item[listKey] === itemId);

        if (!dataItem) {
          return selected;
        }

        return { ...selected, [itemId]: dataItem };
      },
      {}
    );

    const prevCount = Object.values(selectedItems).filter(Boolean).length;
    const nextCount = Object.keys(newSelected).length;

    if (prevCount === nextCount) {
      return;
    }

    // Notify parent so mirrored selection state stays in sync.
    this.setState({ selectedItems: newSelected }, this.handleItemsSelected);
  };

  handleItemsSelected = () => {
    const { onSelectItems } = this.props;

    if (!onSelectItems) {
      return;
    }

    const { selectedItems } = this.state;

    onSelectItems(Object.values(selectedItems).filter(Boolean));
  };

  onSelectItem = (itemListIndex) => () => {
    const { selectedItems } = this.state;
    const { listKey, data } = this.props;
    const selectedTotal = Object.values(selectedItems).filter(Boolean).length;

    // Select All
    if (itemListIndex === void 0 && !selectedTotal) {
      return void this.setState(
        {
          isSelectAllActive: true,
          selectedItems: data.reduce(
            (selected, dataItem) => ({
              ...selected,
              [dataItem[listKey]]: dataItem
            }),
            {}
          )
        },
        this.handleItemsSelected
      );
    }

    // Unselect all
    if (itemListIndex === void 0) {
      return void this.setState(
        { selectedItems: {}, isSelectAllActive: false },
        this.handleItemsSelected
      );
    }

    // Select one
    const item = data[itemListIndex];
    const itemId = item[listKey];

    this.setState(
      ({ selectedItems: latestItems }) => ({
        isSelectAllActive: false,
        selectedItems: {
          ...latestItems,
          [itemId]: latestItems[itemId] ? void 0 : item
        }
      }),
      this.handleItemsSelected
    );
  };

  onSelectBulkAction = (action) => () => {
    const { selectedItems } = this.state;
    const { data, listKey } = this.props;

    action(data.filter((item) => selectedItems[item[listKey]]));
  };

  setSelectedFilters = (changedFilters) => {
    const { selectedFilters } = this.state;
    const mergedFilters = { ...selectedFilters, ...changedFilters };
    const newFilters = Object.keys(mergedFilters).reduce(
      (reducedFilters, filterKey) => {
        const hasValue = mergedFilters[filterKey];

        if (!hasValue) {
          return reducedFilters;
        }

        return { ...reducedFilters, [filterKey]: mergedFilters[filterKey] };
      },
      {}
    );

    this.setState(
      { selectedFilters: newFilters },
      this.callOnFiltersChangeCallback
    );
  };

  callOnFiltersChangeCallback = () => {
    const { onFiltersChange } = this.props;
    const { searchTerm } = this.state;

    if (!onFiltersChange) {
      return;
    }

    onFiltersChange({ filters: this.getSelectedFiltersData(), searchTerm });
  };

  getSelectedFiltersData = () => {
    const { selectedFilters } = this.state;

    // Idempotent on shape. A controlled parent (controlledSelectedFilters) hands us its own
    // already-serialised data — raw values, not { value } options — via the absorb in
    // componentDidUpdate. Re-reading `.value` off those corrupts them (string -> undefined) and
    // throws on the next pass (undefined.value). Only unwrap actual option objects.
    const toData = (value) =>
    value && typeof value === 'object' && 'value' in value ? value.value : value;

    return Object.keys(selectedFilters).reduce(
      (filtersObj, filterName) => {
        const filterValue = selectedFilters[filterName];
        const filterData = Array.isArray(filterValue) ?
        filterValue.map(toData) :
        toData(filterValue);

        return {
          ...filtersObj,
          [filterName]: filterData
        };
      },
      {}
    );
  };

  resetFilters = () => {
    const { initialFilterState = {} } = this.props;
    this.setState(
      { selectedFilters: initialFilterState },
      this.callOnFiltersChangeCallback
    );
  };

  onSearch = ({ target: { value } }) => {
    this.setState({ searchTerm: value }, this.callOnFiltersChangeCallback);
  };

  getView = (hash) => {
    const { renderCardItem, renderListItem, initialPitchList } = this.props;

    const defaultView = initialPitchList ? PITCH_LIST_VIEW : GRID_VIEW;

    const view = hash ? hash.substr(1, hash.length) : defaultView;

    if (view === PITCH_LIST_VIEW) {
      return PITCH_LIST_VIEW;
    }

    if (view !== GRID_VIEW && view !== LIST_VIEW) {
      return renderCardItem ? GRID_VIEW : LIST_VIEW;
    }

    if (view === GRID_VIEW && !renderCardItem) {
      return LIST_VIEW;
    }

    if (view === LIST_VIEW && !renderListItem) {
      return GRID_VIEW;
    }

    return view;
  };

  getNumLoadingItems = (
  size,
  view,
  { fixed }) =>
  {
    const { width = 500, height = 500 } = size;
    const HORIZONTAL_PADDING = 40;
    const CARD_WIDTH = 213;
    const VERTICAL_PADDING = 130;
    const LIST_ITEM_HEIGHT = 135;

    const gridWidth = (width - HORIZONTAL_PADDING) / CARD_WIDTH;
    const listWidth = fixed ?
    NO_INFINITE_LOAD_ITEMS :
    (height - VERTICAL_PADDING) / LIST_ITEM_HEIGHT;
    const numItems = view === GRID_VIEW ? gridWidth : listWidth;

    return parseInt(numItems, 10);
  };

  renderPlaceholder = (
  view,
  containerSize,
  { fixed }) =>
  {
    const { loadingMore, placeholder, loading } = this.props; // ADD PLACEHOLDER
    const { loading: localLoading } = this.state;

    if (!localLoading && !(loading || loadingMore)) {
      return [];
    }

    const { bulkActions, canSelectItems } = this.props;
    const numLoadingItems = this.getNumLoadingItems(containerSize, view, {
      fixed
    });
    const arrayOfItems = [...new Array(numLoadingItems)];

    return arrayOfItems.map((item, index) =>
    // Special case okay to use index as key here
    placeholder ?
    placeholder() // USE PLACEHOLDER IF PROVIDED
    : (
    /* eslint-disable react/no-array-index-key */
    <Placeholder
      key={index}
      view={view}
      hasBulkActions={canSelectItems || Boolean(bulkActions)} />)


    );
  };

  loadMoreResults = async () => {
    const { data, loadMoreResults, totalResults } = this.props;

    if (!loadMoreResults) {
      return Promise.resolve();
    }

    this.setLoading(LOADER_LOAD_MORE_TIMEOUT);

    await loadMoreResults({ currentOffset: data.length, totalResults });

    this.attemptToClearLoadingTimer();

    const { loading } = this.state;

    if (!loading) {
      return null;
    }

    this.setState({ loading: false });

    return null;
  };

  viewRenderFn = (item, index) => {
    const {
      renderListItem,
      renderPitchListItem,
      renderCardItem,
      location
    } = this.props;

    if (renderListItem && this.getView(location.hash) === LIST_VIEW) {
      return renderListItem(item, index);
    }
    if (
    renderPitchListItem &&
    this.getView(location.hash) === PITCH_LIST_VIEW)
    {
      return renderPitchListItem(item, index);
    }
    return renderCardItem && renderCardItem(item, index);
  };

  // These are to provide the exports for List.ListItem & List.CardItem & PitchListItem
  static ListItem = ListItem;

  static CardItem = CardItem;

  static PitchListItem = PitchListItem;

  static CardCaption = CardCaption;

  renderItemCheckbox = ({
    view,
    listItem,
    index




  }) => {
    const { listKey, bulkActions, canSelectItems } = this.props;
    const { gridNodes, selectedItems, hasBulkActions } = this.state;

    if ((!bulkActions || !bulkActions.length) && !canSelectItems) {
      return null;
    }

    if (bulkActions && bulkActions.length && !hasBulkActions) {
      const { permissions } = listItem;

      const allowedActions = bulkActions.filter(
        ({ requiredPermission }) => !requiredPermission ||
        Boolean(permissions && permissions.includes(requiredPermission))
      );

      if (!allowedActions.length && !canSelectItems) {
        return null;
      }

      this.setState({ hasBulkActions: true });
    }

    if (view === LIST_VIEW) {
      return (
        <Checkbox
          onChange={this.onSelectItem(index)}
          checked={Boolean(selectedItems[listItem[listKey]])} />);


    }

    return gridNodes[index] ?
    createPortal(
      <Checkbox
        onChange={this.onSelectItem(index)}
        checked={Boolean(selectedItems[listItem[listKey]])} />,

      gridNodes[index]
    ) :
    null;
  };

  render() {
    const {
      searchTerm,
      bothViewsAvailable,
      usePitchListView,
      selectedFilters,
      selectedItems,
      hasBulkActions,
      loading: localLoading
    } = this.state;

    const {
      name,
      history,
      bulkActions,
      location,
      loading = false,
      loadingMore,
      filters,
      advancedFilters = null,
      renderNoDataPlaceholder,
      listKey,
      children,
      filtersLeftAddon,
      filtersRightAddon,
      totalResults,
      data,
      onFiltersChange,
      onAdvancedFiltersChange,
      hideFilters,
      maxDisplayNum = 0,
      canSelectItems,
      customSearchText,
      customFilterPrefix,
      containerSize,
      numResults,
      stats,
      sortFilters,
      selectedSort,
      onSortFiltersChange,
      containerWidth,
      searchInputPlaceholder,
      isCollapsibleAdvancedFilters,
      shouldReturnOptionAllSelections,
      isAdvancedFilterPersistent,
      initialUpdate,
      getInfiniteScrollParent,
      actionButton,
      featureToolTip,
      hideSearchFilter,
      hideFilterDropdown,
      isSearchBarCollapsed,
      alignSearchRight,
      searchSuffix,
      renderSearchBottomInfo,
      isFixedFilters,
      bulkActionsSuffix
    } = this.props;

    const filteredData = this.getFilteredData(
      data,
      selectedFilters,
      searchTerm,
      onFiltersChange
    );
    const view = this.getView(location.hash);
    const selectedItemsData = data.filter((item) => selectedItems[item[listKey]]);
    const hasFilters = searchTerm || Object.keys(selectedFilters).length;
    const hasInfiniteScrollLoader = loadingMore || localLoading;

    return (
      <SizeMe monitorHeight>
        {({ size }) => {
          const isMobile = isListMobile(containerWidth || size.width);

          const actionButtonProps = {
            ...actionButton,
            isVisible: undefined,
            text: actionButton?.text || '',
            isMobile
          };

          return (
            <div
              className={cx(
                'list-container',
                { 'list-container__fixed-filters': isFixedFilters },
                LIST_CONTAINER({ loading, isMobile })
              )}>

              <div className="list-container__tools-container">
                {!hideFilters &&
                <Filters
                  advanceFilterName={name}
                  history={history}
                  isMobile={isMobile}
                  loading={loading}
                  view={view}
                  filters={filters}
                  advancedFilters={advancedFilters}
                  onUpdateFilters={onFiltersChange}
                  onAdvancedFiltersChange={onAdvancedFiltersChange}
                  sortFilters={sortFilters}
                  selectedSort={selectedSort}
                  onSortFiltersChange={onSortFiltersChange}
                  onChangeView={this.handleChangeView}
                  setSelectedFilters={this.setSelectedFilters}
                  resetFilters={this.resetFilters}
                  selectedFilters={selectedFilters}
                  onSearch={_debounce(this.onSearch, 250)}
                  bothViewsAvailable={bothViewsAvailable}
                  usePitchListView={usePitchListView}
                  filtersLeftAddon={filtersLeftAddon}
                  filtersRightAddon={filtersRightAddon}
                  totalResults={totalResults}
                  numberOfItems={filteredData.length}
                  searchTerm={searchTerm}
                  customSearchText={customSearchText}
                  customPrefix={customFilterPrefix}
                  containerSize={containerSize}
                  numResults={numResults}
                  stats={stats}
                  searchInputPlaceholder={searchInputPlaceholder}
                  isCollapsible={isCollapsibleAdvancedFilters}
                  shouldReturnOptionAllSelections={
                  shouldReturnOptionAllSelections}

                  isAdvancedFilterPersistent={isAdvancedFilterPersistent}
                  initialUpdate={initialUpdate}
                  hideSearchFilter={hideSearchFilter}
                  hideFilterDropdown={hideFilterDropdown}
                  isSearchBarCollapsed={isSearchBarCollapsed}
                  alignSearchRight={alignSearchRight}
                  searchSuffix={searchSuffix}
                  renderSearchBottomInfo={renderSearchBottomInfo} />}



                {actionButton?.isVisible &&
                <>
                    <ListActionButton {...actionButtonProps} />
                    {' '}
                    {featureToolTip && <>{featureToolTip}</>}
                  </>}

              </div>

              {(canSelectItems || hasBulkActions) && filteredData.length ?
              <BulkActions
                actions={bulkActions}
                onSelectAction={this.onSelectBulkAction}
                onSelectAll={this.onSelectItem()}
                totalItems={data.length}
                totalResults={totalResults}
                suffix={bulkActionsSuffix}
                selectedItems={
                Object.values(selectedItems).filter(Boolean).length}

                selectedItemsData={selectedItemsData} /> :

              null}
              {maxDisplayNum && filteredData.length > maxDisplayNum ?
              <div className="list__result-limit">
                  <Icon color="michael" name="alert" size={25} />
                  <p>
                    Too many results to display. Use the filters to find the
                    items you need.
                  </p>
                </div> :
              null}
              <div ref={this._infiniteScrollParentRef} className="list-container__infinite-scroll">
                {(!!data.length || localLoading || loading || loadingMore) &&
                <InfiniteScroll
                  count={totalResults}
                  data={data}
                  onLoadMore={this.loadMoreResults}
                  getScrollableTarget={
                  getInfiniteScrollParent ||
                  this._infiniteScrollParentRef.current}

                  loader={
                  hasInfiniteScrollLoader ?
                  <div
                    key="infinite-scroll-loader"
                    className={cx(
                      'list-container__content',
                      'list-container__infinite-load',
                      {
                        'list-container__infinite-load--grid':
                        view === GRID_VIEW,
                        [CARDS_GRID({ isMobile })]: view === GRID_VIEW
                      }
                    )}>

                          {this.renderPlaceholder(view, size, { fixed: true })}
                        </div> :
                  null}>


                    <div
                    className={cx('list-container__content', {
                      'list-container__content--grid': view === GRID_VIEW,
                      [CARDS_GRID({ isMobile })]: view === GRID_VIEW
                    })}>

                      {loading ?
                    this.renderPlaceholder(view, size, { fixed: true }) :
                    filteredData.
                    slice(0, maxDisplayNum || Number.POSITIVE_INFINITY).
                    map((item, index) =>
                    <div
                      className={cx('list-container__content__item', {
                        'list-container__content__item--selected':
                        selectedItems[item[listKey]]
                      })}
                      key={item[listKey]}>

                              {this.renderItemCheckbox({
                        view,
                        listItem: item,
                        index
                      })}
                              {this.viewRenderFn &&
                      this.viewRenderFn(item, index)}
                            </div>
                    )}
                    </div>
                  </InfiniteScroll>}

              </div>
              {children}
              {filteredData.length === 0 &&
              !loading && (
              hasFilters || customSearchText) ?
              <EmptyDataPlaceholder
                customSearchText={customSearchText}
                searchTerm={searchTerm} /> :

              null}
              {filteredData.length === 0 &&
              !loading &&
              !hasFilters &&
              !customSearchText ?
              setNoDataPlaceholder({
                renderNoDataPlaceholder,
                isMobile
              }) :
              null}
            </div>);

        }}
      </SizeMe>);

  }
}